import Dropdown from "../modal/dropdown";
import ConfirmModal from "../employee/delete_modal";
import DocumentPreviewModal from "../modal/DocumentPreviewModal";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  getTaskAttachments, uploadTaskAttachment, deleteTaskAttachment,
  getTaskComments, addTaskComment, updateTaskComment, deleteTaskComment,
} from "../services/taskservice";
import { toast } from "react-toastify";
import { FaPaperclip, FaTrash, FaEdit, FaCheck, FaTimes, FaDownload, FaCommentAlt, FaInfoCircle, FaUpload, FaEye } from "react-icons/fa";

import { TaskStatus, TaskPriority } from "../../constants/constants";

const STATUSES = [
  { label: "To-Do",      value: TaskStatus.TODO },
  { label: "In Progress",value: TaskStatus.IN_PROGRESS },
  { label: "Done",       value: TaskStatus.DONE },
  { label: "On Hold",    value: TaskStatus.ON_HOLD },
  { label: "Cancelled",  value: TaskStatus.CANCELLED },
];

const PRIORITY_COLORS = {
  [TaskPriority.LOW]:    { bg: "#dcfce7", color: "#16a34a" },
  [TaskPriority.MEDIUM]: { bg: "#fef9c3", color: "#ca8a04" },
  [TaskPriority.HIGH]:   { bg: "#fee2e2", color: "#dc2626" },
  [TaskPriority.URGENT]: { bg: "#fee2e2", color: "#991b1b" },
};

const Badge = ({ text, val, colorMap }) => {
  const key   = val !== undefined ? val : text;
  const style = colorMap?.[key] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <span style={{
      background: style.bg, color: style.color,
      padding: "3px 10px", borderRadius: 99, fontSize: 12,
      fontWeight: 600, textTransform: "capitalize", display: "inline-block",
    }}>
      {typeof text === "string" ? text.replace("_", " ") : text}
    </span>
  );
};

const ViewTask = ({ task, onClose, onEdit, onStatusChange }) => {
  const [activeTab,   setActiveTab]   = useState("details");
  const [statusVal,   setStatusVal]   = useState(task?.status || TaskStatus.TODO);

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [attLoading,  setAttLoading]  = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [deleteAttachmentId, setDeleteAttachmentId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileRef = useRef(null);

  // Comments
  const [comments,     setComments]     = useState([]);
  const [commLoading,  setCommLoading]  = useState(false);
  const [newComment,   setNewComment]   = useState("");
  const [editingId,    setEditingId]    = useState(null);
  const [editText,     setEditText]     = useState("");
  const [posting,      setPosting]      = useState(false);

  /* ── load data per tab ── */
  const loadAttachments = useCallback(async () => {
    setAttLoading(true);
    try {
      const res = await getTaskAttachments(task.id);
      const data = res.data || res || [];
      setAttachments(Array.isArray(data) ? data : data.results || []);
    } catch { toast.error("Failed to load attachments"); }
    finally  { setAttLoading(false); }
  }, [task?.id]);

  const loadComments = useCallback(async () => {
    setCommLoading(true);
    try {
      const res = await getTaskComments(task.id);
      const data = res.data || res || [];
      setComments(Array.isArray(data) ? data : data.results || []);
    } catch { toast.error("Failed to load comments"); }
    finally  { setCommLoading(false); }
  }, [task?.id]);

  useEffect(() => {
    if (!task?.id) return;
    if (activeTab === "attachments") loadAttachments();
    if (activeTab === "comments")    loadComments();
  }, [task, activeTab, loadAttachments, loadComments]);

  /* ── attachment upload ── */
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_name", file.name);
      await uploadTaskAttachment(task.id, fd);
      toast.success("File uploaded");
      await loadAttachments();
    } catch { toast.error("Upload failed"); }
    finally  { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleDeleteAttachment = async () => {
    try {
      await deleteTaskAttachment(task.id, deleteAttachmentId);
      toast.success("Attachment removed");
      setAttachments(a => a.filter(x => x.id !== deleteAttachmentId));
    } catch { toast.error("Failed to delete attachment"); }
    finally { setDeleteAttachmentId(null); }
  };

  /* ── comments ── */
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await addTaskComment(task.id, { content: newComment.trim() });
      setNewComment("");
      await loadComments();
      toast.success("Comment added");
    } catch { toast.error("Failed to add comment"); }
    finally  { setPosting(false); }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim()) return;
    try {
      await updateTaskComment(task.id, commentId, { content: editText.trim() });
      setEditingId(null);
      await loadComments();
      toast.success("Comment updated");
    } catch { toast.error("Failed to update comment"); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteTaskComment(task.id, commentId);
      setComments(c => c.filter(x => x.id !== commentId));
      toast.success("Comment deleted");
    } catch { toast.error("Failed to delete comment"); }
  };

  const handleStatusChange = (val) => {
    setStatusVal(val);
    if (onStatusChange) onStatusChange(task, val);
  };

  if (!task) return null;

  /* ── styles (kept for task-specific content, not the header/tabs) ── */
  const s = {
    grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", marginBottom: 16 },
    label: { fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 },
    value: { fontSize: 14, color: "var(--text-main)", fontWeight: 500 },
    section: { marginBottom: 20 },
    sectionTitle: { fontSize: 12, fontWeight: 700, color: "var(--primary)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid var(--border-color)" },
    uploadBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", background: "var(--primary)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 },
    attRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "var(--bg-main)", borderRadius: 8, border: "1px solid var(--border-color)", marginBottom: 8 },
    iconBtn: (color) => ({ background: "none", border: "none", cursor: "pointer", color, fontSize: 14, padding: 4 }),
    textarea: { width: "100%", padding: "10px 14px", border: "1.5px solid var(--border-color)", borderRadius: 8, fontSize: 14, resize: "vertical", minHeight: 80, outline: "none", boxSizing: "border-box", fontFamily: "inherit", background: "var(--bg-card)", color: "var(--text-main)" },
    postBtn: { padding: "8px 20px", background: "var(--primary)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 },
    commentCard: { background: "var(--bg-main)", borderRadius: 10, border: "1px solid var(--border-color)", padding: "12px 16px", marginBottom: 10 },
    commentMeta: { fontSize: 12, color: "var(--text-muted)", marginBottom: 4 },
    commentText: { fontSize: 14, color: "var(--text-main)" },
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content premium-profile" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>

        <div className="page-header" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 17 }}>Task — {task.task_id || task.id}</h2>
          <button className="profile-close" onClick={onClose}>Back</button>
        </div>

        <div className="profile-tabs">
          <button className={activeTab === "details" ? "active" : ""} onClick={() => setActiveTab("details")}>
            <FaInfoCircle style={{ marginRight: 6 }} /> Details
          </button>
          <button className={activeTab === "attachments" ? "active" : ""} onClick={() => setActiveTab("attachments")}>
            <FaPaperclip style={{ marginRight: 6 }} /> Attachments {attachments.length > 0 && `(${attachments.length})`}
          </button>
          <button className={activeTab === "comments" ? "active" : ""} onClick={() => setActiveTab("comments")}>
            <FaCommentAlt style={{ marginRight: 6 }} /> Comments {comments.length > 0 && `(${comments.length})`}
          </button>
        </div>

        {/* ══════════ DETAILS TAB ══════════ */}
        {activeTab === "details" && (
          <>
            <div style={s.section}>
              <div style={s.sectionTitle}>Task Information</div>
              <div style={s.grid}>
                <div>
                  <div style={s.label}>Task ID</div>
                  <div style={s.value}>{task.task_id || "—"}</div>
                </div>
                <div>
                  <div style={s.label}>Title</div>
                  <div style={s.value}>{task.title}</div>
                </div>
                <div>
                  <div style={s.label}>Priority</div>
                  <div style={{ marginTop: 2 }}><Badge text={task.priority_display || task.priority} val={task.priority} colorMap={PRIORITY_COLORS} /></div>
                </div>
                <div>
                  <div style={s.label}>Status</div>
                  <div style={{ marginTop: 2 }}>
                    <Dropdown options={STATUSES} value={statusVal} onChange={handleStatusChange} placeholder="Status" />
                  </div>
                </div>
              </div>
            </div>

            <div style={s.section}>
              <div style={s.sectionTitle}>Assignment</div>
              <div style={s.grid}>
                <div><div style={s.label}>Assigned To</div><div style={s.value}>{task.assigned_to_name || "—"}</div></div>
                <div><div style={s.label}>Team</div><div style={s.value}>{task.team_name || "—"}</div></div>
                <div><div style={s.label}>Project</div><div style={s.value}>{task.project_name || "—"}</div></div>
                <div><div style={s.label}>Due Date</div><div style={s.value}>{task.due_date || "—"}</div></div>
                <div><div style={s.label}>Start Date</div><div style={s.value}>{task.start_date || "—"}</div></div>
                <div><div style={s.label}>Completed</div><div style={s.value}>{task.completion_date || "—"}</div></div>
              </div>
            </div>

            {task.description && (
              <div style={s.section}>
                <div style={s.sectionTitle}>Description</div>
                <p style={{ fontSize: 14, color: "var(--text-main)", lineHeight: 1.7, margin: 0 }}>{task.description}</p>
              </div>
            )}

            {task.tags && (
              <div style={s.section}>
                <div style={s.sectionTitle}>Tags</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {task.tags.split(",").map((tag, i) => (
                    <span key={i} style={{ background: "rgba(14, 118, 110, 0.1)", color: "var(--primary)", padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 500, border: "1px solid var(--primary-light)" }}>
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ══════════ ATTACHMENTS TAB ══════════ */}
        {activeTab === "attachments" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={s.sectionTitle}>Files & Attachments</div>
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <button
                  style={s.uploadBtn}
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  <FaUpload /> {uploading ? "Uploading…" : "Upload File"}
                </button>
              </div>
            </div>

            {attLoading ? (
              <p style={{ textAlign: "center", color: "var(--text-muted)", padding: 24 }}>Loading attachments…</p>
            ) : attachments.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted)" }}>
                <FaPaperclip style={{ fontSize: 32, marginBottom: 10, opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No attachments yet. Upload a file above.</p>
              </div>
            ) : (
              attachments.map(att => (
                <div key={att.id} style={s.attRow}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <FaPaperclip style={{ color: "var(--primary)", fontSize: 14 }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-main)" }}>{att.original_filename || att.name || "File"}</div>
                      {att.created_at && (
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {new Date(att.created_at).toLocaleDateString()} • {att.file_size_human}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={s.iconBtn("var(--primary)")} title="View" onClick={() => setPreviewDoc(att)}>
                      <FaEye />
                    </button>
                    <a href={att.download_url || att.file_url} download rel="noopener noreferrer" style={s.iconBtn("var(--primary)")} title="Download">
                      <FaDownload />
                    </a>
                    <button style={s.iconBtn("var(--danger)")} onClick={() => setDeleteAttachmentId(att.id)} title="Delete">
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* ══════════ COMMENTS TAB ══════════ */}
        {activeTab === "comments" && (
          <>
            <div style={s.sectionTitle}>Comments</div>

            {/* Add comment */}
            <div style={{ marginBottom: 20 }}>
              <textarea
                style={s.textarea}
                placeholder="Write a comment…"
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button style={s.postBtn} onClick={handleAddComment} disabled={posting || !newComment.trim()}>
                  {posting ? "Posting…" : "Post Comment"}
                </button>
              </div>
            </div>

            {commLoading ? (
              <p style={{ textAlign: "center", color: "var(--text-muted)", padding: 16 }}>Loading comments…</p>
            ) : comments.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px 0", color: "var(--text-muted)" }}>
                <FaCommentAlt style={{ fontSize: 28, marginBottom: 10, opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No comments yet. Be the first to comment!</p>
              </div>
            ) : (
              comments.map(cm => (
                <div key={cm.id} style={s.commentCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: "var(--primary)" }}>
                        {cm.author_name || cm.created_by_name || "User"}
                      </span>
                      <span style={{ ...s.commentMeta, marginLeft: 8 }}>
                        {cm.created_at ? new Date(cm.created_at).toLocaleString() : ""}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {editingId !== cm.id && (
                        <button style={s.iconBtn("#64748b")} onClick={() => { setEditingId(cm.id); setEditText(cm.content); }} title="Edit">
                          <FaEdit />
                        </button>
                      )}
                      <button style={s.iconBtn("#dc2626")} onClick={() => handleDeleteComment(cm.id)} title="Delete">
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  {editingId === cm.id ? (
                    <div style={{ marginTop: 8 }}>
                      <textarea
                        style={{ ...s.textarea, minHeight: 60 }}
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                      />
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 6 }}>
                        <button style={{ ...s.postBtn, background: "#2563eb" }} onClick={() => handleEditComment(cm.id)}>
                          <FaCheck style={{ marginRight: 4 }} /> Save
                        </button>
                        <button style={{ ...s.postBtn, background: "#64748b" }} onClick={() => setEditingId(null)}>
                          <FaTimes style={{ marginRight: 4 }} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ ...s.commentText, margin: "8px 0 0" }}>{cm.content}</p>
                  )}
                </div>
              ))
            )}
          </>
        )}

        {/* ── FOOTER ── */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-color)" }}>
          <div className="employee-actions">
            {onEdit && <button className="primary" onClick={onEdit}>Edit Task</button>}
            <button className="secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      {deleteAttachmentId && (
        <ConfirmModal
          title="Delete Attachment"
          message="Are you sure you want to delete this attachment? This action cannot be undone."
          onConfirm={handleDeleteAttachment}
          onCancel={() => setDeleteAttachmentId(null)}
        />
      )}

      {/* Document Preview Modal */}
      <DocumentPreviewModal 
        doc={previewDoc} 
        onClose={() => setPreviewDoc(null)} 
      />

    </div>
  );
};

export default ViewTask;